<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Have I reconciled my accounts before, and is one in progress right now?"
  Answer:   the primary action (new or resume) and the history table below it.
  Context:  each row's date, counts and who finished it.
  Controls: New/Resume reconciliation; "load more" on the history table.
-->
<script setup lang="ts">
import { systemClock, todayUtc } from '@taler/domain'
import { ActionButton, EmptyState, ErrorState, PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  canReconcile,
  canViewReconciliations,
  ReconciliationHistory,
  useReconciliations,
} from '@/features/reconciliation'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const session = useSession()
const router = useRouter()

const canView = computed(() => canViewReconciliations(session.can))
const canStart = computed(() => canReconcile(session.can))

const reconciliations = useReconciliations({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})

const open = computed(() => reconciliations.summaries.value.find((summary) => summary.status === 'OPEN'))

async function startOrResume(): Promise<void> {
  const outcome = await reconciliations.start(todayUtc(systemClock))
  await router.push({ name: 'reconciliation', params: { id: outcome.id } })
}
</script>

<template>
  <PageShell class="gap-[var(--space-section)]">
    <PageHeader :title="t('reconciliation.list.title')">
      <template #actions>
        <ActionButton
          v-if="canStart"
          size="lg"
          type="button"
          :disabled="reconciliations.isStarting.value"
          @click="startOrResume"
        >
          {{ open !== undefined ? t('reconciliation.list.resume') : t('reconciliation.list.start') }}
        </ActionButton>
      </template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <ErrorState
      v-else-if="session.error.value"
      :title="t('reconciliation.list.errorTitle')"
      :body="t('reconciliation.list.errorBody')"
      :retry-label="t('reconciliation.list.retry')"
      :retrying="session.retrying.value"
      @retry="session.retry()"
    />
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('reconciliation.list.noLedger') }}
    </p>
    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('reconciliation.list.noAccess') }}
    </p>

    <template v-else>
      <p v-if="reconciliations.isLoading.value" role="status">{{ t('reconciliation.list.loading') }}</p>
      <ErrorState
        v-else-if="reconciliations.error.value"
        :title="t('reconciliation.list.errorTitle')"
        :body="t('reconciliation.list.errorBody')"
        :retry-label="t('reconciliation.list.retry')"
        :retrying="reconciliations.retrying.value"
        @retry="reconciliations.retry()"
      />
      <EmptyState
        v-else-if="reconciliations.summaries.value.length === 0"
        :title="t('reconciliation.list.emptyTitle')"
        :body="t('reconciliation.list.emptyBody')"
      />
      <template v-else>
        <ReconciliationHistory :summaries="reconciliations.summaries.value" :locale="currentLocale" />
        <button
          v-if="reconciliations.hasMore.value"
          type="button"
          class="self-start text-[color:var(--color-accent)] underline"
          :disabled="reconciliations.isLoadingMore.value"
          @click="reconciliations.loadMore()"
        >
          {{ t('reconciliation.list.loadMore') }}
        </button>
      </template>
    </template>
  </PageShell>
</template>
